// src/pages/admin/TrashedShiftList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import RestoreIcon from "@mui/icons-material/Restore";

import DataTable from "./DataTable/index";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const TrashedShiftList = () => {
  const { permissions = [], userPermission = [], isAuthReady } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [restoringId, setRestoringId] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasShiftPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasShiftPermission("shift.pages.trashed.view");
  const canCreate =
    hasShiftPermission("shift.pages.create.view") &&
    hasShiftPermission("shift.actions.open_create");
  const canOpenList =
    hasShiftPermission("shift.pages.list.view") &&
    hasShiftPermission("shift.actions.open_list");
  const canRestore = hasShiftPermission("shift.actions.restore");
  const canConfirmRestore = hasShiftPermission("shift.actions.confirm_restore");
  const canCancelRestore = hasShiftPermission("shift.actions.cancel_restore");
  const canViewField = useCallback(
    (field) => hasShiftPermission(`shift.fields.${field}.view`),
    [hasShiftPermission]
  );

  const fetchTrashedShifts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/shifts/trashed/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch trashed shifts");
      }
      const { data } = await res.json();
      setShifts(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error loading trashed shifts", color: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchTrashedShifts();
  }, [canViewPage, fetchTrashedShifts, isAuthReady]);

  const handleRestoreClick = (shift) => {
    if (!canRestore) {
      setSnackbar({ open: true, message: "You do not have permission to restore shifts.", color: "error" });
      return;
    }
    setSelectedShift(shift);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !canConfirmRestore) {
      setSnackbar({ open: true, message: "You do not have permission to confirm restore.", color: "error" });
      return;
    }

    if (!selectedShift) return;

    try {
      setRestoringId(selectedShift.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/shifts/restore/${selectedShift.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Restore failed");
      }
      setSnackbar({ open: true, message: "Shift restored successfully", color: "success" });
      fetchTrashedShifts();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Restore failed", color: "error" });
    } finally {
      setRestoringId(null);
      setRestoreDialogOpen(false);
      setSelectedShift(null);
    }
  };

  const closeRestoreDialog = () => {
    if (!canCancelRestore) return;
    setRestoreDialogOpen(false);
    setSelectedShift(null);
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const columns = [];
  if (canViewField("branch")) columns.push({ Header: "Branch", accessor: "branch" });
  if (canViewField("sub_branch")) columns.push({ Header: "Sub Branch", accessor: "sub_branch" });
  if (canViewField("shift_code")) columns.push({ Header: "Shift Code", accessor: "shift_code" });
  if (canViewField("type")) columns.push({ Header: "Type", accessor: "type" });
  if (canViewField("start_time")) columns.push({ Header: "Start Time", accessor: "start_time" });
  if (canViewField("end_time")) columns.push({ Header: "End Time", accessor: "end_time" });
  if (canViewField("has_flexible_break")) {
    columns.push({ Header: "Flexible Break", accessor: "has_flexible_break" });
  }
  if (canViewField("break_duration")) {
    columns.push({ Header: "Break Duration", accessor: "break_duration" });
  }
  if (canViewField("grace_time")) columns.push({ Header: "Grace Time", accessor: "grace_time" });
  if (canViewField("expected_hours")) columns.push({ Header: "Expected Hours", accessor: "expected_hours" });
  if (canViewField("is_active")) columns.push({ Header: "Status", accessor: "is_active" });
  if (canViewField("deleted_at")) columns.push({ Header: "Deleted At", accessor: "deleted_at" });
  if (canViewField("actions") && canRestore) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = shifts.map((shift) => ({
    branch: shift.branch?.name || shift.branch_name || "N/A",
    sub_branch: shift.sub_branch?.name || shift.sub_branch_name || "N/A",
    shift_code: shift.shift_code || "N/A",
    type: shift.type || "N/A",
    start_time: formatTime(shift.start_time),
    end_time: formatTime(shift.end_time),
    has_flexible_break: shift.has_flexible_break ? "Yes" : "No",
    break_duration: formatDuration(shift.break_duration),
    grace_time: `${shift.grace_time || 0} min`,
    expected_hours: shift.expected_hours ? `${shift.expected_hours / 60}h` : "N/A",
    is_active: shift.deleted_at || !shift.is_active ? "INACTIVE" : "ACTIVE",
    deleted_at: new Date(shift.deleted_at).toLocaleString(),
    actions: (
      <>
        {canRestore && (
          <Tooltip title="Restore Shift">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleRestoreClick(shift)}
              disabled={restoringId === shift.id}
            >
              {restoringId === shift.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RestoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={50} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Trashed Shift List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view trashed shifts.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Shift Management"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Trashed Shift List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/shifts/add")}
              >
                Add New Shift
              </SoftButton>
            )}
            {canOpenList && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>list</Icon>}
                onClick={() => navigate("/shifts")}
              >
                Shift List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        {columns.length > 0 ? (
          <DataTable table={{ columns, rows }} />
        ) : (
          <SoftBox p={3}>
            <SoftTypography variant="body2" color="text">
              No visible fields are assigned for this page.
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>

      <Dialog open={restoreDialogOpen} onClose={closeRestoreDialog}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to restore the shift <strong>{selectedShift?.shift_code}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmRestore && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleRestoreConfirm}
              disabled={restoringId === selectedShift?.id}
              endIcon={restoringId === selectedShift?.id ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {restoringId === selectedShift?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelRestore && (
            <SoftButton variant="contained" color="error" onClick={closeRestoreDialog}>
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrashedShiftList;
